// Extended demo data for the AIP MVP's executive/manager-facing views.
// Strictly mock numbers consistent with the Mi6 cast. No real CRM data.
// Cast (DO NOT confuse):
//   Reps:      Jack Smith, Misty Quota, Will Winsome
//   BDR:       Michael
//   Manager:   The Boss
//   Executive: Hope Springs

import { loadAccounts, type AccountSeed } from "./store";

// Synthetic rep cast — used ONLY for tenant 2 (Mi6 Demo) demo data flows.
// Real tenants pull AE rosters from Supabase via useTenantReps().
export const DEMO_REPS = ["Jack Smith", "Misty Quota", "Will Winsome"] as const;
export type Rep = (typeof DEMO_REPS)[number];

/* -------------------------------------------------------------------------
 * Extended portfolio: 12 mock accounts that include the seeded three plus 9
 * synthetic accounts. We do NOT mutate localStorage — these live only in
 * the extended views.
 * ----------------------------------------------------------------------- */

export interface PortfolioAccount {
  id: string;
  company: string;
  industry: string;
  rep: Rep;
  stage: Stage;
  arr: number; // potential ARR
  amount: number; // current opportunity amount
  closeQuarter: "Q4-26" | "Q1-27";
  pushedDays: number; // days slipped from original close date (0 if not slipped)
  daysInStage: number;
  mpact: number; // 0..100 composite-ish score
  mpactDelta: number; // weekly delta in M-PACT
  hasCompellingEvent: boolean;
  compellingEvent?: string;
  ceConfidence?: "High" | "Medium" | "Low";
  ceDecisionDate?: string; // YYYY-MM-DD
  hasStakeholderMap: boolean;
  hasPlanInFlight: boolean;
  topAccount: boolean;
}

export type Stage =
  | "Suspect"
  | "Prospect"
  | "Discovery"
  | "Qualified Opp"
  | "Proposal"
  | "Negotiation"
  | "Closed Won"
  | "Closed Lost";

const STAGES: Stage[] = [
  "Suspect",
  "Prospect",
  "Discovery",
  "Qualified Opp",
  "Proposal",
  "Negotiation",
  "Closed Won",
  "Closed Lost",
];

// Synthetic accounts added on top of the three seeded ones.
const EXTRA_ACCOUNTS: PortfolioAccount[] = [
  {
    id: "partner-demo",
    company: "Partner Demo Account",
    industry: "Food & Beverage Manufacturing",
    rep: "Jack Smith",
    stage: "Discovery",
    arr: 380000,
    amount: 380000,
    closeQuarter: "Q4-26",
    pushedDays: 21,
    daysInStage: 45,
    mpact: 58,
    mpactDelta: -6,
    hasCompellingEvent: true,
    compellingEvent: "FY27 budget freeze deadline 12/15",
    ceConfidence: "Medium",
    ceDecisionDate: "2026-12-15",
    hasStakeholderMap: true,
    hasPlanInFlight: true,
    topAccount: true,
  },
  {
    id: "riverstone",
    company: "Riverstone Capital",
    industry: "Financial services",
    rep: "Will Winsome",
    stage: "Qualified Opp",
    arr: 520000,
    amount: 520000,
    closeQuarter: "Q4-26",
    pushedDays: 0,
    daysInStage: 18,
    mpact: 74,
    mpactDelta: 4,
    hasCompellingEvent: true,
    compellingEvent: "Audit committee mandate 11/30",
    ceConfidence: "High",
    ceDecisionDate: "2026-11-30",
    hasStakeholderMap: true,
    hasPlanInFlight: true,
    topAccount: true,
  },
  {
    id: "helix",
    company: "Helix Manufacturing",
    industry: "Industrial",
    rep: "Misty Quota",
    stage: "Proposal",
    arr: 240000,
    amount: 240000,
    closeQuarter: "Q4-26",
    pushedDays: 14,
    daysInStage: 32,
    mpact: 51,
    mpactDelta: -9,
    hasCompellingEvent: false,
    hasStakeholderMap: false,
    hasPlanInFlight: true,
    topAccount: false,
  },
  {
    id: "aldrin",
    company: "Aldrin Aerospace",
    industry: "Aerospace",
    rep: "Jack Smith",
    stage: "Negotiation",
    arr: 680000,
    amount: 680000,
    closeQuarter: "Q4-26",
    pushedDays: 0,
    daysInStage: 12,
    mpact: 81,
    mpactDelta: 3,
    hasCompellingEvent: true,
    compellingEvent: "Production line deploy Jan 15",
    ceConfidence: "High",
    ceDecisionDate: "2026-12-20",
    hasStakeholderMap: true,
    hasPlanInFlight: true,
    topAccount: true,
  },
  {
    id: "petra",
    company: "Petra Energy",
    industry: "Energy",
    rep: "Will Winsome",
    stage: "Discovery",
    arr: 290000,
    amount: 290000,
    closeQuarter: "Q1-27",
    pushedDays: 0,
    daysInStage: 22,
    mpact: 49,
    mpactDelta: -1,
    hasCompellingEvent: false,
    hasStakeholderMap: false,
    hasPlanInFlight: false,
    topAccount: true,
  },
  {
    id: "okane",
    company: "Okane Foods",
    industry: "Consumer goods",
    rep: "Misty Quota",
    stage: "Discovery",
    arr: 210000,
    amount: 210000,
    closeQuarter: "Q1-27",
    pushedDays: 0,
    daysInStage: 9,
    mpact: 49,
    mpactDelta: -3,
    hasCompellingEvent: false,
    hasStakeholderMap: true,
    hasPlanInFlight: false,
    topAccount: false,
  },
  {
    id: "vextra",
    company: "Vextra Metals",
    industry: "Metals & Mining",
    rep: "Misty Quota",
    stage: "Qualified Opp",
    arr: 305000,
    amount: 305000,
    closeQuarter: "Q4-26",
    pushedDays: 7,
    daysInStage: 41,
    mpact: 56,
    mpactDelta: -2,
    hasCompellingEvent: false,
    hasStakeholderMap: true,
    hasPlanInFlight: true,
    topAccount: true,
  },
  {
    id: "talia",
    company: "Talia Networks",
    industry: "Telecom",
    rep: "Jack Smith",
    stage: "Proposal",
    arr: 420000,
    amount: 420000,
    closeQuarter: "Q4-26",
    pushedDays: 0,
    daysInStage: 19,
    mpact: 69,
    mpactDelta: 2,
    hasCompellingEvent: true,
    compellingEvent: "Network migration cutover 1/31",
    ceConfidence: "Medium",
    ceDecisionDate: "2026-05-15",
    hasStakeholderMap: true,
    hasPlanInFlight: true,
    topAccount: false,
  },
  {
    id: "kessel",
    company: "Kessel Run Pharma",
    industry: "Pharmaceuticals",
    rep: "Will Winsome",
    stage: "Suspect",
    arr: 95000,
    amount: 95000,
    closeQuarter: "Q1-27",
    pushedDays: 0,
    daysInStage: 6,
    mpact: 31,
    mpactDelta: 0,
    hasCompellingEvent: false,
    hasStakeholderMap: false,
    hasPlanInFlight: false,
    topAccount: false,
  },
];

// Stage mapping for the seeded accounts (so existing /account/:id keeps
// working and Pipeline/Executive views can include them).
const SEED_STAGE_MAP: Record<string, Stage> = {
  northmap: "Discovery",
  bryant: "Qualified Opp",
  westmark: "Suspect",
};

const SEED_REP_MAP: Record<string, Rep> = {
  northmap: "Will Winsome",
  bryant: "Jack Smith", // Hope is exec, not an ae — reassign to Jack for portfolio
  westmark: "Misty Quota",
};

const SEED_EXTRA: Record<
  string,
  Omit<PortfolioAccount, "id" | "company" | "industry" | "rep" | "stage" | "arr" | "amount">
> = {
  northmap: {
    closeQuarter: "Q4-26",
    pushedDays: 0,
    daysInStage: 24,
    mpact: 65,
    mpactDelta: 5,
    hasCompellingEvent: true,
    compellingEvent: "Board review 12/05",
    ceConfidence: "Medium",
    ceDecisionDate: "2026-12-05",
    hasStakeholderMap: true,
    hasPlanInFlight: true,
    topAccount: true,
  },
  bryant: {
    closeQuarter: "Q4-26",
    pushedDays: 0,
    daysInStage: 14,
    mpact: 60,
    mpactDelta: 1,
    hasCompellingEvent: false,
    hasStakeholderMap: true,
    hasPlanInFlight: true,
    topAccount: false,
  },
  westmark: {
    closeQuarter: "Q1-27",
    pushedDays: 0,
    daysInStage: 33,
    mpact: 38,
    mpactDelta: -4,
    hasCompellingEvent: false,
    hasStakeholderMap: false,
    hasPlanInFlight: false,
    topAccount: false,
  },
};

export function buildPortfolio(): PortfolioAccount[] {
  const seeded: PortfolioAccount[] = loadAccounts().map((a: AccountSeed) => ({
    id: a.id,
    company: a.company,
    industry: a.industry,
    rep: SEED_REP_MAP[a.id] ?? "Will Winsome",
    stage: SEED_STAGE_MAP[a.id] ?? (a.stage as Stage) ?? "Discovery",
    arr: a.arr,
    amount: a.arr,
    ...(SEED_EXTRA[a.id] ?? SEED_EXTRA.westmark),
  }));
  return [...seeded, ...EXTRA_ACCOUNTS];
}

/* -------------------------------------------------------------------------
 * Top-line metrics for Executive view.
 * ----------------------------------------------------------------------- */

export interface TopLineMetrics {
  arr: number;
  qualifiedPipeline: number;
  quota: number;
  coverage: number;
  weightedForecast: number;
  compellingEvents: number;
  totalDeals: number;
  pctWithCe: number; // 0..1
}

const STAGE_WEIGHT: Record<Stage, number> = {
  Suspect: 0,
  Prospect: 0.1,
  Discovery: 0.25,
  "Qualified Opp": 0.4,
  Proposal: 0.6,
  Negotiation: 0.8,
  "Closed Won": 1,
  "Closed Lost": 0,
};

export function computeTopLine(p: PortfolioAccount[]): TopLineMetrics {
  const arr = 5_400_000; // committed ARR (mock)
  // Coverage = current-quarter qualified pipeline vs current-quarter quota.
  const qualified = p.filter(
    (a) =>
      a.stage !== "Suspect" &&
      a.stage !== "Closed Lost" &&
      a.closeQuarter === "Q4-26",
  );
  const qualifiedPipeline = qualified.reduce((s, a) => s + a.amount, 0);
  const quota = 1_400_000; // current Q quota
  const coverage = qualifiedPipeline / quota;
  const weightedForecast = p.reduce(
    (s, a) => s + a.amount * STAGE_WEIGHT[a.stage],
    0,
  );
  const compellingEvents = p.filter((a) => a.hasCompellingEvent).length;
  const pctWithCe =
    p.filter((a) => a.hasCompellingEvent).length / Math.max(1, p.length);
  return {
    arr,
    qualifiedPipeline,
    quota,
    coverage,
    weightedForecast,
    compellingEvents,
    totalDeals: p.length,
    pctWithCe,
  };
}

/* -------------------------------------------------------------------------
 * Forecast-accuracy sparkline data — last 8 weeks, % accuracy.
 * ----------------------------------------------------------------------- */

export const FORECAST_ACCURACY_8W: { week: string; pct: number }[] = [
  { week: "W-7", pct: 71 },
  { week: "W-6", pct: 74 },
  { week: "W-5", pct: 78 },
  { week: "W-4", pct: 76 },
  { week: "W-3", pct: 81 },
  { week: "W-2", pct: 84 },
  { week: "W-1", pct: 86 },
  { week: "W-0", pct: 88 },
];

/* -------------------------------------------------------------------------
 * Daily activity per ae (mock). Snapshot for "yesterday".
 * ----------------------------------------------------------------------- */

export interface DailyActivity {
  rep: Rep;
  calls: number;
  emails: number;
  meetings: number;
  cesIdentified: number;
  stakeholderMaps: number;
  plansInFlight: number;
  closedWon: number;
  closedLost: number;
  slipped: number;
  callsAvg7: number;
  emailsAvg7: number;
  meetingsAvg7: number;
}

export const DAILY_ACTIVITY: DailyActivity[] = [
  {
    rep: "Jack Smith",
    calls: 18,
    emails: 42,
    meetings: 5,
    cesIdentified: 2,
    stakeholderMaps: 1,
    plansInFlight: 4,
    closedWon: 0,
    closedLost: 0,
    slipped: 0,
    callsAvg7: 15,
    emailsAvg7: 38,
    meetingsAvg7: 4,
  },
  {
    rep: "Misty Quota",
    calls: 9,
    emails: 21,
    meetings: 2,
    cesIdentified: 0,
    stakeholderMaps: 0,
    plansInFlight: 2,
    closedWon: 0,
    closedLost: 1,
    slipped: 1,
    callsAvg7: 12,
    emailsAvg7: 28,
    meetingsAvg7: 3,
  },
  {
    rep: "Will Winsome",
    calls: 14,
    emails: 33,
    meetings: 4,
    cesIdentified: 1,
    stakeholderMaps: 2,
    plansInFlight: 3,
    closedWon: 1,
    closedLost: 0,
    slipped: 0,
    callsAvg7: 13,
    emailsAvg7: 30,
    meetingsAvg7: 4,
  },
];

/* -------------------------------------------------------------------------
 * Time-of-day activity heatmap (ae × hour).
 * Rows: aes; Cols: 8..18 (8am..6pm). Values 0..10 intensity.
 * ----------------------------------------------------------------------- */

export const HOURS = [8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18];

export const HEATMAP: Record<Rep, number[]> = {
  "Jack Smith":   [2, 6, 9, 8, 4, 3, 7, 9, 8, 5, 2],
  "Misty Quota":  [0, 2, 4, 5, 2, 1, 3, 4, 3, 2, 0],
  "Will Winsome": [1, 4, 7, 7, 3, 2, 6, 8, 7, 4, 1],
};

/* -------------------------------------------------------------------------
 * Rep leaderboard — quota attainment, deals closed, weighted pipeline.
 * ----------------------------------------------------------------------- */

export interface RepRow {
  rep: Rep;
  attainmentPct: number;
  weightedPipeline: number;
  dealsClosed: number;
  cesIdentified: number;
  forecastAccuracy: number;
}

export const LEADERBOARD: RepRow[] = [
  { rep: "Jack Smith",   attainmentPct: 92, weightedPipeline: 1_180_000, dealsClosed: 7, cesIdentified: 6, forecastAccuracy: 91 },
  { rep: "Will Winsome", attainmentPct: 78, weightedPipeline:   840_000, dealsClosed: 5, cesIdentified: 4, forecastAccuracy: 86 },
  { rep: "Misty Quota",  attainmentPct: 54, weightedPipeline:   460_000, dealsClosed: 3, cesIdentified: 1, forecastAccuracy: 72 },
];

/* -------------------------------------------------------------------------
 * Coaching triggers — derived from the portfolio (no formal timeline,
 * aging deals, no stakeholder map on top accounts).
 * ----------------------------------------------------------------------- */

export interface CoachingTrigger {
  rep: Rep;
  accountId: string;
  account: string;
  message: string;
  severity: "high" | "medium" | "low";
}

export function buildCoachingTriggers(
  p: PortfolioAccount[],
): CoachingTrigger[] {
  const out: CoachingTrigger[] = [];

  // Group: deals with no formal timeline by rep
  const byRepNoCe = new Map<Rep, PortfolioAccount[]>();
  for (const a of p) {
    if (
      !a.hasCompellingEvent &&
      a.stage !== "Suspect" &&
      a.stage !== "Closed Lost"
    ) {
      if (!byRepNoCe.has(a.rep)) byRepNoCe.set(a.rep, []);
      byRepNoCe.get(a.rep)!.push(a);
    }
  }
  for (const [rep, list] of byRepNoCe) {
    if (list.length >= 2) {
      out.push({
        rep,
        accountId: list[0].id,
        account: list.map((a) => a.company).join(", "),
        message: `${list.length} deals with no firm or formal timeline identified`,
        severity: list.length >= 3 ? "high" : "medium",
      });
    }
  }

  // Aging deals
  for (const a of p) {
    if (a.daysInStage >= 30 && a.stage !== "Closed Won" && a.stage !== "Closed Lost") {
      out.push({
        rep: a.rep,
        accountId: a.id,
        account: a.company,
        message: `${a.company} deal aging ${a.daysInStage} days in ${a.stage}`,
        severity: a.daysInStage >= 40 ? "high" : "medium",
      });
    }
  }

  // Top account, no stakeholder map
  for (const a of p) {
    if (a.topAccount && !a.hasStakeholderMap) {
      out.push({
        rep: a.rep,
        accountId: a.id,
        account: a.company,
        message: `No stakeholder map on top-account ${a.company}`,
        severity: "high",
      });
    }
  }

  // Slippage
  for (const a of p) {
    if (a.pushedDays >= 14) {
      out.push({
        rep: a.rep,
        accountId: a.id,
        account: a.company,
        message: `${a.company} pushed ${a.pushedDays} days — risk of Q slippage`,
        severity: a.pushedDays >= 21 ? "high" : "medium",
      });
    }
  }

  return out;
}

/* -------------------------------------------------------------------------
 * Utility formatters.
 * ----------------------------------------------------------------------- */

export function fmtUsd(n: number): string {
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(0)}k`;
  return `$${n}`;
}

export function fmtPct(n: number, digits = 0): string {
  return `${(n * 100).toFixed(digits)}%`;
}
